import { Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';

import { environment } from '../../environments/environment';
import { LoginService } from '../auth/services/login.service';
import { ThemeService } from '../theme/services/theme.service';
import { pickAndClean } from '../utilities/json-tools';
import { CompanyService } from './company.service';

@Injectable({
  providedIn: 'root'
})
export class DataDogLoggerService {

  /** @ignore */
  constructor(
    private loginService: LoginService,
    private companyService: CompanyService,
    private themeService: ThemeService
  ) {
    if (environment.datadog?.CLIENT_TOKEN) {
      const { datadog } = environment;

      datadogLogs.init({
        clientToken: datadog.CLIENT_TOKEN,
        // site,
        service: datadog.service,
        env: environment.env,
        version: environment.version,
        forwardErrorsToLogs: true
      });

      this.loginService.getCurrentUser().subscribe(user => {
        if (!user) return;

        // Pick and clean user properties to avoid huge objects in logs
        datadogLogs.setGlobalContextProperty('user', pickAndClean(user, ['id', 'name', 'last_name', 'email', 'admin', 'created_at', 'phone', 'language', 'tenant_id']));
      });

      this.companyService.watch().subscribe(company => {
        if (!company) return;

        // Pick and clean user properties to avoid huge objects in logs
        const market = pickAndClean(company.market, ['id', 'name']);
        datadogLogs.setGlobalContextProperty('company', { ...pickAndClean(company, ['id', 'activity', 'address', 'created_at', 'fiscal_id', 'first_surname', 'name', 'names', 'second_surname']), market });
      });
    }
  }

  /**
   * Logs a message with a given status to Datadog if the CLIENT_TOKEN is configured,
   * otherwise logs to the console using the appropriate method based on the status.
   *
   * @param {'debug' | 'info' | 'warn' | 'error'} status - The severity level of the log message.
   * @param {string} message - The message to be logged.
   * @param {any} error - The error object or additional data to log.
   */
  private call(status: 'debug' | 'info' | 'warn' | 'error', message: string, error: any): void {
    if (environment.datadog?.CLIENT_TOKEN) {
      datadogLogs.logger.log(message, undefined, status, error);
      return;
    }

    const logMessage = `${this.themeService.theme.title}\r\n${message}\r\n`;
    const logMethod = {
      error: console.error,
      warn: console.warn,
      debug: console.debug,
      info: console.info
    }[status] || console.info;

    logMethod(logMessage, error);
  }

  /** Log a custom error in dataDog */
  public error(message: string, error?: any): void {
    this.call('error', message, error);
  }

  public warn(message: string, error: any): void {
    this.call('warn', message, error);
  }

  public info(message: string, error: any = null): void {
    this.call('info', message, error);
  }

  public debug(message: string, error: any = null): void {
    this.call('debug', message, error);
  }
}